/**
 * GenerateControls Component
 * Provides AI-powered composition generation interface
 */

import { useState, useCallback, useEffect } from 'react';
import { generateAPI } from '../../services/api/GenerateAPI';
import { useAuthContext } from '../../contexts/AuthContext';
import { useCredits, InsufficientCreditsError } from '../../hooks/useCredits';
import { activityTrackingService } from '../../services/user/ActivityTrackingService';
import type { Composition } from '../../types/composition';

/**
 * Props for GenerateControls component
 */
export interface GenerateControlsProps {
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Current composition for edit mode */
  currentComposition?: Composition;
  /** Callback when composition is generated */
  onCompositionGenerated: (composition: Composition) => void;
  /** Callback when insufficient credits */
  onInsufficientCredits?: (required: number, available: number) => void;
}

/**
 * GenerateControls component
 * Renders AI generation prompt textarea and generate button
 */
export function GenerateControls({
  isAuthenticated,
  currentComposition,
  onCompositionGenerated,
  onInsufficientCredits,
}: GenerateControlsProps) {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusColor, setStatusColor] = useState('');
  const [loadingDots, setLoadingDots] = useState('');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [progressBar, setProgressBar] = useState(0);
  const { signIn } = useAuthContext();
  
  // Get credits from useCredits hook
  const { credits, consumeCredit, hasCredits } = useCredits();

  // Retro game-style loading messages
  const loadingMessages = [
    '🎮 LOADING SOUND CHIP',
    '🎵 COMPOSING MELODY',
    '🎹 ARRANGING CHANNELS',
    '🎸 TUNING INSTRUMENTS',
    '🥁 PROGRAMMING DRUMS',
    '🎼 MIXING TRACKS',
    '✨ ADDING 8-BIT MAGIC',
    '🔊 ADJUSTING VOLUMES',
    '🎶 CRAFTING HARMONIES',
    '⚡ POWERING UP SYNTHS',
    '🌟 FINALIZING COMPOSITION',
    '🎺 CALIBRATING FREQUENCIES',
    '🎻 LAYERING MELODIES',
    '🔮 GENERATING PATTERNS',
    '💫 SYNCING RHYTHMS',
    '🎪 ORCHESTRATING SOUNDS',
    '🌈 PAINTING AUDIO WAVES',
    '🚀 LAUNCHING SEQUENCES',
    '⭐ POLISHING NOTES',
    '🎭 BALANCING DYNAMICS',
    '🔥 IGNITING CREATIVITY',
    '💎 REFINING TONES',
    '🌙 MOONWALKING BASSLINES',
    '☄️ DROPPING THE BEAT',
    '🎯 TARGETING PERFECTION',
  ];

  /**
   * Check if composition has any notes (for edit mode detection)
   */
  const hasNotes = useCallback((): boolean => {
    if (!currentComposition) return false;
    return currentComposition.channels.some((ch) => ch.notes.length > 0);
  }, [currentComposition]);

  /**
   * Update status message with color
   */
  const updateStatus = useCallback((message: string, color: string = '') => {
    setStatusMessage(message);
    setStatusColor(color);
  }, []);

  /**
   * Animate loading dots
   */
  useEffect(() => {
    if (isGenerating) {
      const interval = setInterval(() => {
        setLoadingDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setLoadingDots('');
    }
    return undefined;
  }, [isGenerating]);

  /**
   * Rotate loading messages and animate progress bar
   */
  useEffect(() => {
    if (isGenerating) {
      // Rotate messages every 3 seconds
      const messageInterval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);

      // Animate progress bar with logarithmic slowdown (fake progress for visual feedback)
      let progress = 0;
      const progressInterval = setInterval(() => {
        // Slow down as we approach 90% - more realistic feel
        const remaining = 90 - progress;
        const increment = Math.max(0.5, remaining * 0.08); // Slower as it gets closer
        progress = Math.min(progress + increment, 90); // Cap at 90% until actually done
        setProgressBar(progress);
      }, 1000); // Update every second

      return () => {
        clearInterval(messageInterval);
        clearInterval(progressInterval);
      };
    } else {
      setLoadingMessageIndex(0);
      setProgressBar(0);
    }
    return undefined;
  }, [isGenerating, loadingMessages.length]);

  /**
   * Clear status message after delay
   */
  useEffect(() => {
    if (statusMessage && !isGenerating) {
      const timer = setTimeout(() => {
        setStatusMessage('');
        setStatusColor('');
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [statusMessage, isGenerating]);

  /**
   * Handle generate button click
   */
  const handleGenerate = useCallback(async () => {
    // Validate prompt
    if (!prompt.trim()) {
      updateStatus('⚠️ Please enter a prompt first.', 'crimson');
      return;
    }

    // Check authentication
    if (!isAuthenticated) {
      updateStatus('⚠️ Please sign in to use AI generation.', 'crimson');
      return;
    }

    // Check credits before generation
    const requiredCredits = 1;
    if (!hasCredits(requiredCredits)) {
      updateStatus('❌ Insufficient credits. Please purchase more credits.', 'crimson');
      // Notify parent component to show insufficient credits modal
      onInsufficientCredits?.(requiredCredits, credits);
      return;
    }

    // Start generation
    setIsGenerating(true);
    setLoadingMessageIndex(0);
    setProgressBar(0);

    // Determine mode (new or edit)
    const isEditing = hasNotes();

    try {
      // Call AI generation API
      const result = await generateAPI.generate(
        prompt.trim(),
        isEditing ? currentComposition : undefined
      );

      // Consume credit after successful generation
      try {
        const remainingCredits = await consumeCredit('generate', requiredCredits);
        
        // Complete progress bar
        setProgressBar(100);

        // Apply generated composition
        onCompositionGenerated(result);

        // Track AI generation activity (silently - don't block user on failure)
        try {
          await activityTrackingService.trackAIGeneration({
            prompt: prompt.trim(),
            mode: isEditing ? 'edit' : 'new',
            timestamp: Date.now(),
          });
        } catch (trackingError) {
          // Log error but don't show to user - tracking failures should be silent
          console.warn('[GenerateControls] Activity tracking failed:', trackingError);
        }

        // Show success message
        updateStatus(
          `✅ LEVEL COMPLETE! (${remainingCredits} credits remaining)`,
          'green'
        );

        // Clear prompt after successful generation
        setPrompt('');
      } catch (creditError) {
        // Handle credit consumption errors
        if (creditError instanceof InsufficientCreditsError) {
          updateStatus('❌ Insufficient credits. Please purchase more credits.', 'crimson');
          onInsufficientCredits?.(requiredCredits, credits);
        } else {
          console.error('[GenerateControls] Failed to consume credit:', creditError);
          updateStatus('❌ Failed to deduct credit. Please try again.', 'crimson');
        }
      }
    } catch (error) {
      console.error('[GenerateControls] Generation failed', error);
      
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      
      updateStatus(`❌ Generation failed: ${errorMessage}`, 'crimson');
    } finally {
      setIsGenerating(false);
    }
  }, [
    prompt,
    isAuthenticated,
    credits,
    currentComposition,
    hasNotes,
    hasCredits,
    consumeCredit,
    onCompositionGenerated,
    onInsufficientCredits,
    updateStatus,
  ]);

  /**
   * Handle prompt textarea change
   */
  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setPrompt(e.target.value);
    },
    []
  );

  /**
   * Handle Enter key in textarea (Ctrl+Enter to generate)
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleGenerate();
      }
    },
    [handleGenerate]
  );

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return (
      <section id="generate-section" aria-label="AI music generation" style={{ marginBottom: '1rem' }}>
        <div style={{
          width: '91.666667%',
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'var(--color-surface)',
          border: '4px solid var(--color-accent)',
          borderRadius: '4px',
          textAlign: 'center',
          padding: '1.5rem'
        }}>
          <div style={{
            fontFamily: 'var(--font-family)',
            fontSize: '0.75rem',
            color: 'var(--color-fg)'
          }}>
            🔒{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                signIn();
              }}
              style={{
                color: 'var(--color-accent)',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
              aria-label="Sign in to use AI generation"
            >
              Sign in
            </a>
            {' '}to use AI generation
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="generate-section" aria-label="AI music generation" style={{ marginBottom: '1rem' }}>
      <h2 className="sr-only">AI-Powered Music Generation</h2>
      <div
        id="generate-controls"
        style={{
          width: '91.666667%',
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'var(--color-surface)',
          padding: '0.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          fontFamily: 'var(--font-family)',
          fontSize: '0.75rem'
        }}
      >
        {/* Status Message / Loading Screen */}
        {isGenerating ? (
          <div
            id="generate-loading"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
              animation: 'fadeIn 0.3s ease-in',
              padding: '1rem 0'
            }}
          >
            {/* Loading Message */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              fontSize: '0.9rem',
              color: 'var(--color-accent)',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              <span style={{ 
                display: 'inline-block',
                animation: 'pulse 1.5s ease-in-out infinite',
                fontSize: '1.5rem'
              }}>
                🎮
              </span>
              <span style={{ animation: 'slideIn 0.5s ease-out' }}>
                {loadingMessages[loadingMessageIndex]}{loadingDots}
              </span>
            </div>

            {/* Progress Bar */}
            <div style={{
              width: '100%',
              maxWidth: '800px',
              margin: '0 auto',
              height: '2.5rem',
              background: 'var(--color-surface)',
              border: '3px solid var(--color-cell-border)',
              borderRadius: '4px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div
                style={{
                  height: '100%',
                  width: `${progressBar}%`,
                  background: 'var(--color-accent)',
                  transition: 'width 0.8s ease-out',
                  boxShadow: '0 0 10px var(--color-accent)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Animated scanline effect */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
                  animation: 'scanline 1s linear infinite'
                }} />
              </div>
              {/* Progress percentage */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                color: progressBar > 50 ? 'var(--color-bg)' : 'var(--color-fg)',
                textShadow: progressBar > 50 ? 'none' : '0 0 4px var(--color-bg)',
                pointerEvents: 'none'
              }}>
                {Math.round(progressBar)}%
              </div>
            </div>

            {/* Retro loading tip */}
            <div style={{
              fontSize: '0.7rem',
              color: 'var(--color-fg)',
              opacity: '0.7',
              textAlign: 'center',
              animation: 'blink 2s ease-in-out infinite',
              letterSpacing: '0.05em'
            }}>
              ⚡ PLEASE WAIT - DO NOT POWER OFF ⚡
            </div>
          </div>
        ) : (
          <div
            id="generate-status"
            style={{ 
              minHeight: '1.25rem',
              color: statusColor || 'var(--color-fg)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            aria-live="polite"
          >
            <span>{statusMessage}</span>
          </div>
        )}

        {/* Prompt Input and Generate Button */}
        <div id="generate-inputs" style={{ display: 'flex', gap: '4px' }}>
          <textarea
            id="generate-prompt"
            rows={2}
            value={prompt}
            onChange={handlePromptChange}
            onKeyDown={handleKeyDown}
            disabled={isGenerating}
            placeholder="Describe the type of 8-bit track you want (e.g., 'upbeat battle theme' or 'peaceful village music')..."
            aria-label="Music generation prompt"
            style={{
              flex: '1',
              background: 'var(--color-surface)',
              border: '4px solid var(--color-cell-border)',
              color: 'var(--color-fg)',
              padding: '0.5rem',
              fontSize: '0.65rem',
              resize: 'vertical',
              borderRadius: '4px',
              fontFamily: 'var(--font-family)',
              opacity: isGenerating ? '0.5' : '1',
              cursor: isGenerating ? 'not-allowed' : 'text'
            }}
          />
          <button
            id="generate-btn"
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            aria-label="Generate music from prompt"
            style={{
              background: 'var(--color-surface)',
              border: '4px solid var(--color-cell-border)',
              color: 'var(--color-fg)',
              padding: '0.5rem 1rem',
              fontSize: '0.65rem',
              borderRadius: '4px',
              fontFamily: 'var(--font-family)',
              cursor: (isGenerating || !prompt.trim()) ? 'not-allowed' : 'pointer',
              opacity: (isGenerating || !prompt.trim()) ? '0.5' : '1'
            }}
          >
            {isGenerating ? (
              <i className="fa-solid fa-spinner fa-spin"></i>
            ) : (
              '🛠️ Generate'
            )}
          </button>
        </div>

        {/* Helper Text */}
        {!isGenerating && (
          <div style={{ fontSize: '0.6rem', color: 'var(--color-fg)', opacity: '0.7' }}>
            {hasNotes()
              ? '💡 Tip: AI will modify your current composition based on your prompt'
              : '💡 Tip: Click "Fresh Slate" before generating to start from scratch'}
          </div>
        )}
      </div>
    </section>
  );
}
